import { useState, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import clientInfo from 'common/lib/clientInfo';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';

type ChatContext = {
  contextToken: string;
  pagePath: string;
};

const getCachedContext = (key: string) => {
  let cachedContext: ChatContext = {
    contextToken: '',
    pagePath: '',
  };

  try {
    const item = JSON.parse(localStorage.getItem(key) || '{}');
    const { contextToken = '', pagePath = '' } = item;
    cachedContext = {
      contextToken,
      pagePath,
    };
  } catch (e) {
    copyMagicLog({
      message: 'useChatContext: error parsing cached context',
      key,
    });
  }

  return cachedContext;
};

const isValidContext = (context: ChatContext | null) => {
  if (!context) {
    return false;
  }

  if (!context.contextToken) {
    return false;
  }

  if (!context.pagePath) {
    return false;
  }

  return true;
};

const useChatContext = (initialContext: ChatContext) => {
  const { contextToken, pagePath } = initialContext;
  const [chatContext, setChatContext] = useState<ChatContext>({
    contextToken,
    pagePath,
  });

  const storageKey = 'kjb_copyMagic_conversationAssistant'.concat(
    '_userId',
    clientInfo.get('user_id'),
    '_chatContextV2',
  );

  const storeChatContext = useCallback(() => {
    localStorage.setItem(storageKey, JSON.stringify(chatContext));
    copyMagicLog({
      message: 'useChatContext: storeChatContext',
      key: storageKey,
      chatContext,
    });
  }, [chatContext, storageKey]);

  const restoreChatContext = useCallback(() => {
    const cachedContext = getCachedContext(storageKey);

    if (isValidContext(cachedContext) && !isEqual(chatContext, cachedContext)) {
      setChatContext(cachedContext);
      copyMagicLog({
        message: 'useChatContext: restoreChatContext',
        key: storageKey,
        chatContext: cachedContext,
      });
    }
  }, [chatContext, storageKey]);

  return {
    chatContext,
    storeChatContext,
    restoreChatContext,
  };
};

export default useChatContext;
